import { updateWorkoutItemGroup } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EditableInput, useToast } from "ui";
import { useDebouncedCallback } from "use-debounce";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";

export interface EditableGroupNameProps {
  workoutItemId: string;
  isLoading?: boolean;
}

export const EditableGroupName = observer(function EditableGroupName(
  props: EditableGroupNameProps,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const { withSaving } = useAuthenticatedNavigationContext();

  const workoutEditStore = useWorkoutEditStore();
  const workoutItem = workoutEditStore.getWorkoutItemById(props.workoutItemId);
  const name = workoutEditStore.getWorkoutItemName(workoutItem.id);

  const saveName = useDebouncedCallback(async (newName: string) => {
    try {
      await withSaving(
        updateWorkoutItemGroup({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: workoutItem.id,
          },
          body: { name: newName },
        }),
      );
    } catch {
      workoutEditStore.updateWorkoutItem(workoutItem.id, {
        name: workoutEditStore.getWorkoutItemName(workoutItem.id),
      });
      toast.toast({
        title: t("editWorkout.updateGroupError.title"),
        description: t("editWorkout.updateGroupError.description"),
        variant: "destructive",
      });
    }
  }, 300);

  useEffect(() => {
    return () => {
      saveName.flush();
    };
  }, [workoutEditStore, workoutItem.id]);

  function handleChange(newName: string) {
    workoutEditStore.updateWorkoutItem(workoutItem.id, { name: newName });
    saveName(newName);
  }

  if (props.isLoading) {
    return (
      <div className={"flex w-full items-center justify-between px-2"}>
        <div className={"text-left"}>
          {name || t("editWorkout.defaultGroupName")}
        </div>
        <Icon
          name="tabler-loader-2"
          className="animate-spin text-muted-foreground"
        />
      </div>
    );
  }

  return (
    <EditableInput
      value={name}
      onChange={handleChange}
      placeholder={t("editWorkout.defaultGroupName")}
    />
  );
});
